import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  listAllCoreDepartments,
  listAllCoreEmployees,
  listCoreAddresses,
  listCoreContacts,
  listCoreCustomers,
  listCoreLocations,
  listCoreMemberships,
  listCoreOrganizations,
  listCoreProfessionals,
  listCoreTenants,
  listRolesForOrganization,
  fetchUserLabels,
  type CoreAddress,
  type CoreContact,
  type CoreCustomer,
  type CoreDepartment,
  type CoreEmployee,
  type CoreLocation,
  type CoreMembership,
  type CoreOrganization,
  type CoreProfessional,
  type CoreRole,
  type CoreTenant,
  type UserLabel,
} from '@/lib/zifekCore';
import OrganizationsPanel from './components/OrganizationsPanel';
import LocationsPanel from './components/LocationsPanel';
import MembershipsPanel from './components/MembershipsPanel';
import AddressesPanel from './components/AddressesPanel';
import ContactsPanel from './components/ContactsPanel';
import CustomersPanel from './components/CustomersPanel';
import ProfessionalsPanel from './components/ProfessionalsPanel';
import RolesPanel from './components/RolesPanel';
import DepartmentsPanel from './components/DepartmentsPanel';
import EmployeesPanel from './components/EmployeesPanel';
import OrgChartPanel from './components/OrgChartPanel';

type Tab =
  | 'organizations'
  | 'locations'
  | 'memberships'
  | 'roles'
  | 'contacts'
  | 'customers'
  | 'professionals'
  | 'departments'
  | 'employees'
  | 'orgchart'
  | 'addresses';

const TABS: { key: Tab; label: string; icon: string }[] = [
  { key: 'organizations', label: 'Organisations', icon: 'ri-building-2-line' },
  { key: 'orgchart', label: 'Organigramme', icon: 'ri-organization-chart' },
  { key: 'locations', label: 'Établissements', icon: 'ri-store-2-line' },
  { key: 'memberships', label: 'Membres', icon: 'ri-group-line' },
  { key: 'departments', label: 'Départements', icon: 'ri-node-tree' },
  { key: 'employees', label: 'Employés', icon: 'ri-team-line' },
  { key: 'roles', label: 'Rôles & permissions', icon: 'ri-shield-user-line' },
  { key: 'contacts', label: 'Contacts', icon: 'ri-contacts-book-2-line' },
  { key: 'customers', label: 'Clients', icon: 'ri-user-star-line' },
  { key: 'professionals', label: 'Professionnels', icon: 'ri-briefcase-4-line' },
  { key: 'addresses', label: 'Adresses', icon: 'ri-map-pin-2-line' },
];

export default function SuperAdminCorePage() {
  const [tab, setTab] = useState<Tab>('organizations');
  const [tenants, setTenants] = useState<CoreTenant[]>([]);
  const [organizations, setOrganizations] = useState<CoreOrganization[]>([]);
  const [locations, setLocations] = useState<CoreLocation[]>([]);
  const [memberships, setMemberships] = useState<CoreMembership[]>([]);
  const [addresses, setAddresses] = useState<CoreAddress[]>([]);
  const [contacts, setContacts] = useState<CoreContact[]>([]);
  const [customers, setCustomers] = useState<CoreCustomer[]>([]);
  const [professionals, setProfessionals] = useState<CoreProfessional[]>([]);
  const [departments, setDepartments] = useState<CoreDepartment[]>([]);
  const [employees, setEmployees] = useState<CoreEmployee[]>([]);
  const [roles, setRoles] = useState<CoreRole[]>([]);
  const [users, setUsers] = useState<Record<number, UserLabel>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [t, o, l, m, a, ct, cu, pr, dp, em, rl] = await Promise.all([
        listCoreTenants(),
        listCoreOrganizations(),
        listCoreLocations(),
        listCoreMemberships(),
        listCoreAddresses(),
        listCoreContacts(),
        listCoreCustomers(),
        listCoreProfessionals(),
        listAllCoreDepartments(),
        listAllCoreEmployees(),
        listRolesForOrganization(null),
      ]);
      setTenants(t);
      setOrganizations(o);
      setLocations(l);
      setMemberships(m);
      setAddresses(a);
      setContacts(ct);
      setCustomers(cu);
      setProfessionals(pr);
      setDepartments(dp);
      setEmployees(em);
      setRoles(rl);

      const userIds = [
        ...o.map((x) => x.owner_user_id || 0),
        ...m.map((x) => x.user_id),
        ...employees.map((x) => x.user_id || 0),
        ...employees.map((x) => x.manager_user_id || 0),
        ...departments.map((x) => x.manager_user_id || 0),
      ];
      const labels = await fetchUserLabels(userIds);
      setUsers(labels);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger Zifek Core.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const tenantNameById = useMemo(() => {
    const map: Record<number, string> = {};
    tenants.forEach((t) => { map[t.id] = t.name; });
    return map;
  }, [tenants]);

  const orgNameById = useMemo(() => {
    const map: Record<number, string> = {};
    organizations.forEach((o) => { map[o.id] = o.commercial_name || o.legal_name || `Org #${o.id}`; });
    return map;
  }, [organizations]);

  const locationCount = useMemo(() => {
    const map: Record<number, number> = {};
    locations.forEach((l) => {
      const key = l.organization_id || 0;
      map[key] = (map[key] || 0) + 1;
    });
    return map;
  }, [locations]);

  const memberCount = useMemo(() => {
    const map: Record<number, number> = {};
    memberships.forEach((m) => {
      const key = m.organization_id || 0;
      map[key] = (map[key] || 0) + 1;
    });
    return map;
  }, [memberships]);

  const ownerLabel = useMemo(() => {
    const map: Record<number, string> = {};
    organizations.forEach((o) => {
      const u = o.owner_user_id ? users[o.owner_user_id] : undefined;
      map[o.owner_user_id || -1] = u?.name || (o.owner_user_id ? `#${o.owner_user_id}` : '—');
    });
    return map;
  }, [organizations, users]);

  const stats = [
    { label: 'Organisations', value: organizations.length, icon: 'ri-building-2-line', tone: 'primary' },
    { label: 'Membres', value: memberships.length, icon: 'ri-group-line', tone: 'secondary' },
    { label: 'Départements', value: departments.length, icon: 'ri-node-tree', tone: 'accent' },
    { label: 'Employés', value: employees.length, icon: 'ri-team-line', tone: 'foreground' },
  ] as const;

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-organization-chart text-primary-600"></i>
            Zifek Core
          </h2>
          <p className="text-sm text-foreground-500 mt-1">
            Socle central : Organisations, Établissements, Membres, Rôles, Départements, Employés, Contacts, Clients & Professionnels.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70 disabled:opacity-50 transition-colors"
        >
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
          Actualiser
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        {stats.map((s) => (
          <div key={s.label} className="bg-background-50 border border-background-200/70 rounded-lg p-4">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${
              s.tone === 'primary' ? 'bg-primary-100 text-primary-700'
                : s.tone === 'secondary' ? 'bg-secondary-100 text-secondary-700'
                : s.tone === 'accent' ? 'bg-accent-100 text-accent-900'
                : 'bg-foreground-100 text-foreground-700'
            }`}>
              <i className={`${s.icon} text-lg`}></i>
            </div>
            <div className="text-xl font-bold font-heading text-foreground-950">{s.value}</div>
            <div className="text-xs text-foreground-500">{s.label}</div>
          </div>
        ))}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {error}
          </span>
          <button onClick={load} className="text-xs underline cursor-pointer whitespace-nowrap">Réessayer</button>
        </div>
      )}

      <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 mb-5 w-full sm:w-fit overflow-x-auto">
        {TABS.map((tb) => (
          <button
            key={tb.key}
            onClick={() => setTab(tb.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
              tab === tb.key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            <i className={tb.icon}></i>
            {tb.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
        </div>
      ) : (
        <>
          {tab === 'organizations' && (
            <OrganizationsPanel
              organizations={organizations}
              tenantNameById={tenantNameById}
              locationCount={locationCount}
              memberCount={memberCount}
              ownerLabel={ownerLabel}
            />
          )}
          {tab === 'orgchart' && (
            <OrgChartPanel
              organizations={organizations}
              departments={departments}
              employees={employees}
              users={users}
            />
          )}
          {tab === 'locations' && <LocationsPanel locations={locations} />}
          {tab === 'memberships' && <MembershipsPanel memberships={memberships} users={users} />}
          {tab === 'departments' && (
            <DepartmentsPanel
              departments={departments}
              employees={employees}
              orgNameById={orgNameById}
              users={users}
            />
          )}
          {tab === 'employees' && (
            <EmployeesPanel
              employees={employees}
              departments={departments}
              orgNameById={orgNameById}
              users={users}
            />
          )}
          {tab === 'roles' && <RolesPanel roles={roles} />}
          {tab === 'contacts' && <ContactsPanel contacts={contacts} orgNameById={orgNameById} />}
          {tab === 'customers' && <CustomersPanel customers={customers} orgNameById={orgNameById} />}
          {tab === 'professionals' && <ProfessionalsPanel professionals={professionals} orgNameById={orgNameById} />}
          {tab === 'addresses' && <AddressesPanel addresses={addresses} orgNameById={orgNameById} />}
        </>
      )}
    </div>
  );
}